import React from 'react'
import { makeStyles } from '@material-ui/styles'

const useStyles = makeStyles(theme => ({
  resizer: {
    width: ({ resizerWidth }) => `${resizerWidth}px`,
    cursor: 'ew-resize',
    background: 'transparent',
    position: 'absolute',
    top: 0,
    right: ({ resizerWidth }) => `-${resizerWidth / 2}px`,
    height: '100%'
  }
}))

/**
 * A horizontal resizer component that allows users to adjust the width of a component in percentage.
 *
 * @param {Object} props
 * @param {React.RefObject} props.containerRef - Reference to the container element that holds the resizer.
 * @param {Function} props.setWidthPercent - Function to set the width of the left component as a percentage.
 * @param {number} [props.defaultWidthPercent=50] - Default width percentage for the component.
 * @param {number} [props.minWidthPercent=10] - Minimum width percentage allowed for the component.
 * @param {number} [props.maxWidthPercent=90] - Maximum width percentage allowed for the component.
 * @param {number} [props.resizerWidth=10] - Width of the resizer div itself in pixels. The higher this value, the wider the resize button will be.
 *
 * @returns
 */
const PatientViewResizer = ({
  containerRef,
  setWidthPercent = percent => {},
  defaultWidthPercent = 50,
  minWidthPercent = 10,
  maxWidthPercent = 90,
  resizerWidth = 10
}) => {
  const classes = useStyles({ resizerWidth })

  const handleMouseDown = () => {
    const onMouseMove = e => {
      const container = containerRef?.current
      if (!container) {
        return
      }

      document.body.style.userSelect = 'none' // disable selection globally while resizing
      const containerWidth = container.getBoundingClientRect().width
      const newWidth = ((e.clientX - container.getBoundingClientRect().left) / containerWidth) * 100
      const clampedWidth = Math.max(minWidthPercent, Math.min(newWidth, maxWidthPercent))
      setWidthPercent(clampedWidth)
    }

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      document.body.style.userSelect = '' // re-enable selection globally after resizing
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const handleResetWidth = () => {
    setWidthPercent(defaultWidthPercent)
  }

  return <div className={classes.resizer} onMouseDown={handleMouseDown} onDoubleClick={handleResetWidth} />
}

export default PatientViewResizer
